/**
 * The briefing plate's text block — how much of a sortie's prose the plate can
 * hold, and where the editable values go.
 *
 * `patrol-briefing.png` shows what this plate is *for*: three rows of the
 * original's Mission Setup sentence with seven boxed values in it
 * (`[three]`, `[Sopwith Camels]`, `[echelon]`, `[regular]`, `[daytime]`,
 * `[light]`, `[partly cloudy]`). It is a parameter sheet, not a narrative.
 *
 * This port's generators write a *briefing* rather than that sentence:
 * `spec.briefing` is the three `PS.EXE` template sentences that carry the boxed
 * values, sometimes with a situation paragraph in front of them and a colour
 * note behind. It does not fit — a Hawker-Meets-His-Match briefing wraps to
 * eleven rows and the plate holds five or six.
 *
 * **So the block is sized by its controls, not by its prose.** The paragraphs
 * that carry a boxed value are laid out first and always fit; whatever rows are
 * left over go to the narrative, marked with an ellipsis when there was more of
 * it. That ordering is the point: the boxed values are the *only* way to change
 * `region`, the sun, the wind and the sky, so a plate that clipped them would
 * cost the screen the thing it exists for. `mission-setup-layout.test.ts` pins
 * it over every sortie the catalog can generate.
 *
 * Nothing is lost to the trim: `MISSION ASSIGNMENT`, the very next screen,
 * writes the situation and the plan out under their own headings.
 */

import { SLOT_HEIGHT } from "../render/plate-chrome.ts";
import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import { boxValues, segmentRows, wrapText, type BoxedValue, type Segment } from "./panel-parts.ts";

/**
 * `patrol-briefing.png`'s lower plate is full-bleed — (0,120) to the bottom
 * edge, with the action row drawn *on* it.
 */
export const BRIEFING_PLATE: Rect = { x: 0, y: 120, w: 320, h: 80 };
export const BRIEFING_TEXT_X = 14;
export const BRIEFING_COLUMN = 292;
/** The action row sits here on this screen (`patrol-briefing.png`), not at `ACTION_ROW_Y`. */
export const BRIEFING_ACTION_Y = 176;

/** A slot box is drawn from two pixels above its text row, so its ink ends this far below it. */
const SLOT_DESCENT = SLOT_HEIGHT - 2;
/** The last pixel row the text block may touch: one above the action row. */
const TEXT_BOTTOM = BRIEFING_ACTION_Y - 1;

/**
 * Where the block starts and how tightly it is set, widest setting first. The
 * capture's own y=127 / pitch 10 is used whenever the values fit its five rows,
 * which is every sortie but the longest. A sixth row does not exist there — its
 * slot box would land at y=175, inside the action row — so the block moves up
 * to the top edge of the plate (y=122 puts the slot's own frame exactly on
 * y=120) and tightens by a pixel, which is the most the plate can hold.
 */
const SETTINGS: readonly { readonly y: number; readonly pitch: number }[] = [
  { y: 127, pitch: 10 },
  { y: 122, pitch: 9 },
];

/** How many text rows a setting has between its first row and the action row. */
export function briefingCapacity(setting: { readonly y: number; readonly pitch: number }): number {
  return Math.floor((TEXT_BOTTOM - SLOT_DESCENT - setting.y) / setting.pitch) + 1;
}

const WORD_CHAR = /[A-Za-z0-9]/;

/** Whether `value` appears in `text` as a whole word — `boxValues`' own rule, so the split agrees with the boxing. */
function carriesValue(text: string, value: string): boolean {
  if (value === "") return false;
  for (let from = 0; ; ) {
    const at = text.indexOf(value, from);
    if (at < 0) return false;
    const before = text[at - 1];
    const after = text[at + value.length];
    if (!(before && WORD_CHAR.test(before)) && !(after && WORD_CHAR.test(after))) return true;
    from = at + 1;
  }
}

export interface BriefingBlock {
  /** Rows of plain prose above the parameter sentence — as many as were left over, possibly none. */
  readonly narrative: readonly string[];
  /** The parameter sentence, split into plain and boxed segments. */
  readonly segments: readonly Segment[];
  readonly y: number;
  readonly pitch: number;
  /** Row budget for `segments`, counted from `y + narrative.length * pitch`. */
  readonly maxLines: number;
}

/** The rows the narrative may use, ellipsised when it had more to say. */
function narrativeRows(paragraphs: readonly string[], budget: number, font: FontAtlas): string[] {
  if (budget <= 0 || paragraphs.length === 0) return [];
  const lines = wrapText(font, paragraphs.join(" "), BRIEFING_COLUMN);
  if (lines.length <= budget) return lines;
  return [...lines.slice(0, budget - 1), "..."];
}

/**
 * Splits a sortie's briefing into the parameter sentence — which always fits —
 * and however much narrative the remaining rows hold.
 */
export function briefingBlock(paragraphs: readonly string[], values: readonly BoxedValue[], font: FontAtlas): BriefingBlock {
  const carrying = paragraphs.filter((paragraph) => values.some((entry) => carriesValue(paragraph, entry.value)));
  const rest = paragraphs.filter((paragraph) => !carrying.includes(paragraph));
  const segments = boxValues(carrying.join(" "), values);
  const needed = segmentRows(segments, BRIEFING_COLUMN, font);
  const setting = SETTINGS.find((candidate) => needed <= briefingCapacity(candidate)) ?? SETTINGS[SETTINGS.length - 1];
  if (!setting) throw new Error("shell: the briefing plate has no text settings");
  const capacity = briefingCapacity(setting);
  const narrative = narrativeRows(rest, capacity - needed, font);
  return { narrative, segments, y: setting.y, pitch: setting.pitch, maxLines: capacity - narrative.length };
}
